// --- React ---
import React, { useState, useEffect } from 'react';

// --- 다국어 ---
import { useTranslation } from 'react-i18next';

// --- 공통 UI 컴포넌트 ---
import { Button, ModalShell, Input, FormStack, FormField } from '@/components/ui';

// --- API ---
import { useUpdateDevice } from '@/api/device';

// --- 스토어 ---
import { useAppStore } from '@/store/loadingStore';
import { useNotificationStore } from '@/store/notificationStore';
import { useModalStore } from '@/store/modalStore';

// --- 유틸 ---
import { parseSiteApiError } from '@/utils/errors';

// --- i18n ---
import { i18nKeys } from '@/i18n/i18nKeyGen';

// ==================== 타입 정의 ====================

interface DeviceEditForm {
  cloud_name: string;
  cloud_user: string;
  cloud_desc: string;
  fixed_latitude: number | null;
  fixed_longitude: number | null;
}

interface DeviceEditModalProps {
  device: { id: number; cloud_name?: string | null; cloud_user?: string | null; cloud_desc?: string | null; fixed_latitude?: number | null; fixed_longitude?: number | null } | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// ==================== 컴포넌트 ====================

const DeviceEditModal: React.FC<DeviceEditModalProps> = ({
  device,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { showLoading, hideLoading } = useAppStore();
  const { showSuccess, showError } = useNotificationStore();
  const { showAlert } = useModalStore();
  const updateDeviceMutation = useUpdateDevice();

  // ==================== 상태 관리 ====================

  const [editForm, setEditForm] = useState<DeviceEditForm>({
    cloud_name: '',
    cloud_user: '',
    cloud_desc: '',
    fixed_latitude: null,
    fixed_longitude: null,
  });

  // ==================== 이펙트 ====================

  /**
   * 디바이스 데이터가 변경되면 폼 초기값을 디바이스 정보로 세팅.
   */
  useEffect(() => {
    if (device) {
      setEditForm({
        cloud_name: device.cloud_name || '',
        cloud_user: device.cloud_user || '',
        cloud_desc: device.cloud_desc || '',
        fixed_latitude: device.fixed_latitude ?? null,
        fixed_longitude: device.fixed_longitude ?? null,
      });
    }
  }, [device]);

  // ==================== 핸들러 ====================

  /**
   * 디바이스 정보 저장.
   * 흐름: 로딩 표시 → API 호출 → 성공 시 콜백 → 모달 닫기
   */
  const handleSave = async () => {
    if (!device) return;

    try {
      showLoading(t(i18nKeys.site.deviceInfoUpdating));

      await updateDeviceMutation.mutateAsync({
        id: device.id,
        data: editForm,
      });

      await onSuccess();
      showSuccess(i18nKeys.site.deviceInfoEdited);
      onClose();
    } catch (error: unknown) {
      console.error('❌ [DeviceEditModal] 에러:', error);
      const e = parseSiteApiError(error);

      if (e.message === 'device:max_devices_exceeded') {
        showAlert(
          t(i18nKeys.device.max_devices_exceeded_title),
          t(i18nKeys.device.max_devices_exceeded_desc),
          undefined,
          'danger'
        );
      } else {
        showError(i18nKeys.site.deviceEditFailed, e.message);
      }
    } finally {
      hideLoading();
    }
  };

  // ==================== 렌더링 ====================

  return (
    <ModalShell
      isOpen={isOpen}
      title={t(i18nKeys.site.deviceInfoEdit)}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button onClick={onClose} variant="secondary">
            {t(i18nKeys.site.cancel)}
          </Button>
          <Button
            onClick={handleSave}
            variant="primary"
            disabled={updateDeviceMutation.isPending}
          >
            {t(i18nKeys.site.save)}
          </Button>
        </>
      }
    >
      <FormStack>
        {/* Cloud Name 입력 */}
        <FormField>
          <Input
            label="Cloud Name"
            value={editForm.cloud_name}
            onChange={value => setEditForm({ ...editForm, cloud_name: value })}
            fullWidth
          />
        </FormField>

        {/* Cloud User 입력 */}
        <FormField>
          <Input
            label="Cloud User"
            value={editForm.cloud_user}
            onChange={value => setEditForm({ ...editForm, cloud_user: value })}
            fullWidth
          />
        </FormField>

        {/* Cloud Description 입력 */}
        <FormField>
          <Input
            label="Cloud Description"
            multiline
            rows={3}
            value={editForm.cloud_desc}
            onChange={value => setEditForm({ ...editForm, cloud_desc: value })}
            fullWidth
          />
        </FormField>

        {/* 고정 위도 입력 */}
        <FormField>
          <Input
            label={t(i18nKeys.site.fixedLatitude)}
            type="number"
            value={editForm.fixed_latitude !== null ? String(editForm.fixed_latitude) : ''}
            onChange={value => {
              const parsed = value === '' ? null : parseFloat(value);
              setEditForm({ ...editForm, fixed_latitude: isNaN(parsed as number) ? null : parsed });
            }}
            placeholder={t(i18nKeys.site.exampleLatitude)}
            fullWidth
          />
        </FormField>

        {/* 고정 경도 입력 */}
        <FormField>
          <Input
            label={t(i18nKeys.site.fixedLongitude)}
            type="number"
            value={editForm.fixed_longitude !== null ? String(editForm.fixed_longitude) : ''}
            onChange={value => {
              const parsed = value === '' ? null : parseFloat(value);
              setEditForm({
                ...editForm,
                fixed_longitude: isNaN(parsed as number) ? null : parsed,
              });
            }}
            placeholder={t(i18nKeys.site.exampleLongitude)}
            fullWidth
          />
        </FormField>
      </FormStack>
    </ModalShell>
  );
};

export default DeviceEditModal;
